import { RAD, naturalEarth, greatCircle } from "./geo"
import { LAND } from "./land"

const STEPS = 64

// A flat Natural Earth world from 84°N to 57°S fitted inside the box. A portrait phone zooms in
// towards centerLongitude instead of showing a thin strip.
export function flatView(width, height, { padding, centerLongitude = 0 }) {
  const [ right ] = naturalEarth(Math.PI, 0)
  const [ , top ] = naturalEarth(0, 84 * RAD)
  const [ , bottom ] = naturalEarth(0, -57 * RAD)
  const innerWidth = width - padding.left - padding.right
  const innerHeight = height - padding.top - padding.bottom
  let scale = Math.min(innerWidth / (2 * right), innerHeight / (top - bottom))
  let ox = padding.left + innerWidth / 2
  if (innerWidth < innerHeight) {
    scale = Math.min(innerHeight / (top - bottom), scale * 2.5)
    const [ center ] = naturalEarth(centerLongitude * RAD, 0)
    ox = Math.min(padding.left + right * scale, Math.max(padding.left + innerWidth - right * scale, padding.left + innerWidth / 2 - center * scale))
  }
  const oy = padding.top + (innerHeight - (top - bottom) * scale) / 2 + top * scale
  return {
    flat: true,
    dot: Math.max(0.8, Math.min(LAND.step * RAD * 0.87 * scale * 0.5, 4)),
    wrap: Math.PI * 0.87 * scale,
    project([ lng, lat ]) {
      const [ x, y ] = naturalEarth(lng * RAD, lat * RAD)
      return [ ox + x * scale, oy - y * scale ]
    }
  }
}

// An orthographic globe turned to face [lng, lat]. Points on the far side project to null.
export function globeView(width, height, { padding, lng, lat }) {
  const radius = Math.min(width - padding.left - padding.right, height - padding.top - padding.bottom) / 2
  const cx = padding.left + (width - padding.left - padding.right) / 2
  const cy = padding.top + (height - padding.top - padding.bottom) / 2
  const sin0 = Math.sin(lat * RAD)
  const cos0 = Math.cos(lat * RAD)
  return {
    flat: false,
    cx,
    cy,
    radius,
    dot: Math.max(0.8, Math.min(LAND.step * RAD * radius * 0.45, 3)),
    project([ pointLng, pointLat ]) {
      const lambda = (pointLng - lng) * RAD
      const phi = pointLat * RAD
      const cosPhi = Math.cos(phi)
      if (sin0 * Math.sin(phi) + cos0 * cosPhi * Math.cos(lambda) < 0) return null
      return [ cx + radius * cosPhi * Math.sin(lambda), cy - radius * (cos0 * Math.sin(phi) - sin0 * cosPhi * Math.cos(lambda)) ]
    }
  }
}

export function drawLand(ctx, view, points, width, height) {
  ctx.clearRect(0, 0, width, height)
  if (!view.flat) {
    ctx.fillStyle = "#f4ead6"
    ctx.beginPath()
    ctx.arc(view.cx, view.cy, view.radius, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.fillStyle = "#ddd0b6"
  const size = view.dot
  for (const point of points) {
    const xy = view.project(point)
    if (!xy) continue
    const [ x, y ] = xy
    if (x < -size || y < -size || x > width + size || y > height + size) continue
    ctx.fillRect(x - size / 2, y - size / 2, size, size)
  }
}

// Screen points along the great circle between two [lng, lat] places, with null where the
// route wraps around the edge of a flat map or goes behind a globe.
export function routePoints(view, from, to) {
  const along = greatCircle(from, to)
  const points = []
  let previous = null
  for (let i = 0; i <= STEPS; i++) {
    const point = view.project(along(i / STEPS))
    if (point && previous && view.flat && Math.abs(point[0] - previous[0]) > view.wrap) points.push(null)
    points.push(point)
    previous = point
  }
  return points
}

// Strokes the part of a polyline between fractions a and b of its points.
export function stroke(ctx, points, a = 0, b = 1) {
  const last = points.length - 1
  const from = a * last
  const to = b * last
  ctx.beginPath()
  let pen = false
  for (let i = Math.floor(from); i <= Math.ceil(to); i++) {
    const point = interpolate(points, Math.min(to, Math.max(from, i)))
    if (!point) { pen = false; continue }
    pen ? ctx.lineTo(point[0], point[1]) : ctx.moveTo(point[0], point[1])
    pen = true
  }
  ctx.stroke()
}

export function pointAt(points, u) {
  return interpolate(points, u * (points.length - 1))
}

function interpolate(points, t) {
  const i = Math.floor(t)
  const a = points[i]
  const b = points[Math.min(points.length - 1, i + 1)]
  const f = t - i
  if (!a) return null
  if (f === 0) return a
  if (!b) return null
  return [ a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f ]
}
